import React, { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { Product, PrescriptionData } from '../../types/domain';

/**
 * The lab step of the order wizard — what PrescriptionForm is for a medication.
 *
 * A lab panel has no sig, no quantity to choose and no days supply. What the
 * requisition actually needs is a diagnosis code and the collection details,
 * so those replace the prescription fields rather than sitting alongside them.
 */

export interface LabOrderDetails {
  diagnosisCodes: string[];
  fasting: boolean;
  collectionNotes: string;
}

interface LabTestFormProps {
  product: Product;
  onSave: (prescription: PrescriptionData, lab: LabOrderDetails) => void;
  onCancel: () => void;
  initialData?: LabOrderDetails;
}

export const LabTestForm: React.FC<LabTestFormProps> = ({
  product,
  onSave,
  onCancel,
  initialData
}) => {
  const [diagnosisCodes, setDiagnosisCodes] = useState(
    (initialData?.diagnosisCodes ?? product.lab?.defaultDiagnosisCodes ?? []).join(', ')
  );
  const [fasting, setFasting] = useState(initialData?.fasting ?? false);
  const [collectionNotes, setCollectionNotes] = useState(initialData?.collectionNotes ?? '');

  const handleSave = () => {
    onSave(
      {
        productId: product._id,
        // A requisition is for one panel; there is nothing to order two of.
        quantity: 1,
        sig: '',
        pharmacyNotes: '',
        daysSupply: 0
      },
      {
        diagnosisCodes: diagnosisCodes.split(',').map(c => c.trim()).filter(Boolean),
        fasting,
        collectionNotes: collectionNotes.trim()
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-4 bg-violet-50 border border-violet-100 rounded-xl">
        <FlaskConical className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-gray-900">{product.displayName}</p>
          <p className="text-gray-600 mt-1">
            A collection kit is mailed to the patient. They take the sample at home, post it
            back, and results come back to the clinic automatically.
          </p>
          <p className="text-gray-700 text-sm mt-2 font-medium">
            Turnaround is typically 7–10 days from order to results, depending on
            mailing and the lab.
          </p>
          {product.lab?.specimenSource && (
            <p className="text-gray-500 text-xs mt-1">Specimen: {product.lab.specimenSource}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Diagnosis Codes
        </label>
        <input
          type="text"
          value={diagnosisCodes}
          onChange={(e) => setDiagnosisCodes(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Comma-separated ICD-10, e.g., E03.9, R53.83"
        />
        <p className="text-xs text-gray-500 mt-1">
          Pre-filled from the panel's defaults. Sent to the lab with the requisition.
        </p>
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={fasting}
          onChange={(e) => setFasting(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">
          Fasting sample
          <span className="block text-xs text-gray-400">
            The patient should not eat before collecting.
          </span>
        </span>
      </label>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Collection Notes
        </label>
        <textarea
          value={collectionNotes}
          onChange={(e) => setCollectionNotes(e.target.value)}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Optional notes for the lab"
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Add to Order
        </button>
      </div>
    </div>
  );
};
